import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import Drawer from "@mui/material/Drawer";
import { useDispatch, useSelector } from "react-redux";
import {
  getFunctionalGoals,
  postFunctionalGoalReviewMeetingTranscriptNotes,
  resetTranscriptNotes,
} from "../../store/actions";
// imported as components (not <img>) so their fill/stroke can be themed via css
import { ReactComponent as DeleteIcon } from "../../assets/images/trash.svg";
import { ReactComponent as AddIcon } from "../../assets/images/add.svg";
import { AiOutlineCaretLeft, AiOutlineCaretRight } from "react-icons/ai";

// note boxes reuse the AI drawer's card look, squared corners rounded off
const noteBoxStyle = {
  width: "100%",
  borderRadius: "8px",
  resize: "none",
};

function TranscriptPreviewPopUp({
  open,
  onClose,
  progressData,
  nextSteps,
  readonlyPermissions,
}) {
  const dispatch = useDispatch();
  const selectedSubMenuPermission = useSelector(
    (state) => state.selectedSubMenuPermission
  );
  const transcriptNotesData = useSelector((state) => state.transcriptNotesData);
  // the whole batch is saved with one call, so one success response drives the refresh
  const transcriptNotesResponse = useSelector(
    (state) => state.postTranscriptNotesResponse
  );
  const [progressItems, setProgressItems] = useState([]);
  const [nextStepItems, setNextStepItems] = useState([]);
  const [newProgress, setNewProgress] = useState("");
  const [newNextStep, setNewNextStep] = useState("");
  const [aiFullScreenMode, setAiFullScreenMode] = useState(false);
  const handledAddResponse = useRef(null);

  const canWrite =
    (selectedSubMenuPermission === "write" ||
      selectedSubMenuPermission === "admin") &&
    readonlyPermissions !== "read";

  useEffect(() => {
    if (transcriptNotesData && transcriptNotesData.status === "Success") {
      setProgressItems(transcriptNotesData.progress || []);
      setNextStepItems(transcriptNotesData.next_steps || []);
    } else if (!transcriptNotesData) {
      setProgressItems([]);
      setNextStepItems([]);
    }
  }, [transcriptNotesData]);

  const closeDrawer = () => {
    // close first; the notes stay rendered until the exit animation is done,
    // otherwise the drawer flashes the empty/loading state while sliding out
    if (onClose) onClose();
  };

  const handleDrawerExited = () => {
    setProgressItems([]);
    setNextStepItems([]);
    setNewProgress("");
    setNewNextStep("");
    dispatch(resetTranscriptNotes());
  };

  const updateItem = (setItems, index, value) => {
    setItems((items) => items.map((item, i) => (i === index ? value : item)));
  };

  const removeItem = (setItems, index) => {
    setItems((items) => items.filter((item, i) => i !== index));
  };

  const addItem = (setItems, value, setValue) => {
    if (value.trim() === "") return;
    setItems((items) => [...items, value.trim()]);
    setValue("");
  };

  useEffect(() => {
    // the page lists are only refreshed once the notes are really saved
    if (!transcriptNotesResponse) return;
    if (handledAddResponse.current === transcriptNotesResponse) return;
    handledAddResponse.current = transcriptNotesResponse;
    dispatch(
      getFunctionalGoals(
        localStorage.getItem("bi_id"),
        localStorage.getItem("product_id"),
        localStorage.getItem("functional_goal_id"),
        localStorage.getItem("goal_review_id")
      )
    );
    closeDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcriptNotesResponse]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Skip notes already saved against this meeting; the API skips exact duplicates
    const toKeys = (data) =>
      (data || []).map((item) => (item.value || "").trim().toLowerCase());

    const existingProgress = toKeys(progressData);
    const existingNextSteps = toKeys(nextSteps);
    const newNotes = (items, existing) =>
      items
        .map((item) => (item || "").trim())
        .filter((text) => text && !existing.includes(text.toLowerCase()));

    const progressToAdd = newNotes(progressItems, existingProgress);
    const nextStepsToAdd = newNotes(nextStepItems, existingNextSteps);

    if (progressToAdd.length === 0 && nextStepsToAdd.length === 0) {
      closeDrawer();
      return;
    }

    // one call for the whole batch; the drawer closes on that success response
    dispatch(
      postFunctionalGoalReviewMeetingTranscriptNotes(
        localStorage.getItem("functional_goal_id"),
        localStorage.getItem("goal_review_id"),
        progressToAdd,
        nextStepsToAdd
      )
    );
  };

  const renderNotes = (
    label,
    hint,
    items,
    setItems,
    newItem,
    setNewItem,
    placeholder
  ) => (
    <div style={{ width: "90%" }}>
      <div
        style={{
          marginTop: "20px",
          marginBottom: "10px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "10px",
        }}
      >
        <div
          style={{
            flexShrink: 0,
            fontSize: "var(--sub-heading-font-size)",
            fontWeight: "var(--heading-font-weight)",
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: "14px",
            color: "#646464",
            textAlign: "right",
          }}
        >
          {hint}
        </div>
      </div>

      {items.map((item, index) => (
        <div
          key={index}
          className="transcript-note-card"
          style={{
            width: "100%",
            marginTop: "10px",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <textarea
            className="transcript-note-box"

            style={noteBoxStyle}
            rows={2}
            value={item}
            onChange={(e) => updateItem(setItems, index, e.target.value)}
          />
          <DeleteIcon
            className="transcript-icon-delete"
            width={14}
            height={14}
            role="img"
            aria-label="Delete Icon"
            style={{ cursor: "pointer", marginLeft: "10px" }}
            onClick={() => removeItem(setItems, index)}
          />
        </div>
      ))}

      <div
        className="transcript-note-card"
        style={{
          width: "100%",
          marginTop: "10px",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <textarea
          className="transcript-note-box"
          style={noteBoxStyle}
          value={newItem}
          placeholder={placeholder}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addItem(setItems, newItem, setNewItem);
            }
          }}
        />
        <AddIcon
          className="transcript-icon-add"
          width={14}
          height={14}
          role="img"
          aria-label="Add Icon"
          style={{ cursor: "pointer", marginLeft: "10px" }}
          onClick={() => addItem(setItems, newItem, setNewItem)}
        />
        
      </div>
    </div>
  );

  // the assistant's own line about the extraction; static hint while loading or if it is missing
  const reviewHint =
    transcriptNotesData?.status === "Success" && transcriptNotesData.message
      ? transcriptNotesData.message
      : "Review the extracted notes, edit or remove anything before adding them to this meeting.";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={transcriptNotesData ? closeDrawer : null}
      transitionDuration={{ enter: 500, exit: 300 }}
      SlideProps={{
        easing: {
          enter: "cubic-bezier(0.0, 0, 0.2, 1)", // standard
          exit: "cubic-bezier(0.4, 0.0, 1, 1)", // ease-out
          transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        // clear the extracted notes only once the drawer has finished sliding out
        onExited: handleDrawerExited,
      }}
      PaperProps={{
        sx: {
          boxShadow: "none",
          borderLeft: "1px solid lightGray",
        },
      }}
      sx={{
        "& .MuiDrawer-paper": {
          padding: 0,
          height: "calc(100vh - 50px)",
          marginTop: "64px",
          width: aiFullScreenMode ? "calc(100% - 50px)" : "50%",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "transparent",
          },
        },
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px",
            borderBottom: "1px solid lightGray",
          }}
        >
          <div
            style={{
              maxWidth: "60%",
              textOverflow: "ellipsis",
              overflow: "hidden",
              paddingTop: "4px",
              fontSize: "var(--sub-heading-font-size)",
              fontWeight: "var(--heading-font-weight)",
            }}
          >
            Review and Edit Extracted Progress Notes and Next Steps
          </div>

          <div>
            {!aiFullScreenMode ? (
              <a
                href="#"
                className="fullscreen"
                style={{ fontSize: "25px", marginRight: "10px", color: "gray" }}
                onClick={() => setAiFullScreenMode(true)}
              >
                <AiOutlineCaretRight />
              </a>
            ) : (
              <a
                href="#"
                className="fullscreen"
                style={{ fontSize: "25px", marginRight: "10px", color: "gray" }}
                onClick={() => setAiFullScreenMode(false)}
              >
                <AiOutlineCaretLeft />
              </a>
            )}

            <Button
              onClick={() => closeDrawer()}
              style={{
                border: "2px solid darkgray",
                borderRadius: "10px",
                color: "gray",
                backgroundColor: "white",
              }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              form="transcript-notes-form"
              style={{
                width: "fit-content",
                color: "white",
                backgroundColor: "#0a5f59",
                border: "3px solid #0a5f59",
                marginLeft: "20px",
                padding: "4px 10px",
              }}
              disabled={
                !canWrite ||
                (progressItems.length === 0 && nextStepItems.length === 0)
              }
            >
              Add to Meeting
            </Button>
          </div>
        </div>

        {/* Content */}
        <form
          id="transcript-notes-form"
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "start",
            alignItems: "center",
            backgroundColor: "rgba(248, 252, 251, 0.62)",
            flexGrow: 1,
            overflowY: "auto",
            overflowX: "hidden",
            paddingBottom: "100px",
          }}
        >
          
          {!transcriptNotesData && (
            <div
              role="status"
              aria-label="Extracting notes from the transcript"
              style={{ padding: "20px", width: "100%" }}
            >
              <div className="ai-loader" aria-hidden="true">
                <div className="line"></div>
                <div className="line"></div>
                <div className="line"></div>
                <div className="line short"></div>

                <div className="gap"></div>

                <div className="line"></div>
                <div className="line"></div>
                <div className="line"></div>
                <div className="line"></div>
              </div>
            </div>
          )}

          {transcriptNotesData && transcriptNotesData.status === "Error" && (
            <div
              style={{
                width: "90%",
                marginTop: "20px",
                color: "#D32F2F",
                fontSize: "var(--text-font-size)",
              }}
            >
              {transcriptNotesData.message ||
                "Could not extract notes from the transcript."}
            </div>
          )}

          {transcriptNotesData && transcriptNotesData.status === "Success" && (
            <>
            <div
                style={{
                  width: "90%",
                  marginTop: "20px",
                  fontSize: "18px",
                  color: "#646464",
                }}
              >
                {reviewHint}
              </div>

              {renderNotes(
                "Progress Notes",
                "Accomplishments this period and any route to green",
                progressItems,
                setProgressItems,
                newProgress,
                setNewProgress,
                "Add a progress note and press Enter"
              )}
              {renderNotes(
                "Next Steps",
                "Key activities in next 2 weeks",
                nextStepItems,
                setNextStepItems,
                newNextStep,
                setNewNextStep,
                "Add a next step and press Enter"
              )}
            </>
          )}
        </form>
      </div>
    </Drawer>
  );
}
export default TranscriptPreviewPopUp;

